// frontend/app/components/lessonPlans/assetSummary.js
//
// Whether an asset block offers "What is this Play With Object (PWO)?" /
// "Summarize this video segment", and what pressing it puts in front of the
// class.
//
// Pure and shared, because the workspace and present mode have to agree. They
// DRAW the control differently — a quiet second control under a card, a chrome
// button under a full-stage scene — but which blocks have one at all, what it
// is called, and what it produces is one decision and belongs in one place.
//
// TWO PATHS, AND WHY THE AUTHORED ONE IS PRIMARY.
//
// `summary` is text the lesson author wrote. Pressing the control shows it:
// no request, no wait, no variation. That matters more here than anywhere else
// in the app. This is read out to a room, and a teacher running the same lesson
// again in period 4 needs the same words that were on the wall in period 2 —
// they have already said "as it said earlier" to thirty people. A live ask
// cannot promise same, and cannot promise fast either; a class does not wait
// well.
//
// `summary_prompt` is for the author who would rather Saras answer live, and
// goes down the ask path the ai_prompt blocks already use.
//
// A block with neither gets NO CONTROL. A button that reveals nothing teaches a
// teacher to stop trusting the buttons — the same rule presentRules.revealFor
// keeps for Reveal.
//
// The topic quiz is deliberately absent from the label table: a quiz is not a
// thing to summarize before you have taken it, and a block type with no label
// gets no control anywhere.

/** The control's label, per asset type. Source English; callers run it through t(). */
export const SUMMARY_LABELS = {
  saras_pwo: "What is this Play With Object (PWO)?",
  saras_video: "Summarize this video segment",
};

const textOf = (value) =>
  typeof value === "string" && value.trim().length > 0 ? value.trim() : null;

/**
 * What this block's summary control offers, or null if it has none.
 *
 *   { kind: "authored", label, text, prompt: null }  — show the authored words
 *   { kind: "prompt",   label, text: null, prompt }  — ask Saras this
 *
 * Authored text wins when a block carries both. An author who wrote the words
 * meant them to be the answer; the prompt beside them is the fallback they
 * were replacing, and quietly preferring the slower, non-deterministic path
 * over the one they typed would be the wrong way round.
 */
export const assetSummaryFor = (block) => {
  const label = SUMMARY_LABELS[block?.type];
  if (!label) return null;

  const authored = textOf(block.summary);
  if (authored) {
    return { kind: "authored", label, text: authored, prompt: null };
  }

  const prompt = textOf(block.summary_prompt);
  if (prompt) {
    return { kind: "prompt", label, text: null, prompt };
  }

  return null;
};

export const hasAssetSummary = (block) => assetSummaryFor(block) !== null;
